import { toastr } from 'react-redux-toastr';
import { PRODUCTS_ACTION_TYPES, PRODUCTS_CONFIG } from './actionTypes';
import { productsApi } from './api';
import { selectProductById } from "./selectors";
import { geoSelectors } from '../geolocation';

export const productsActions = {
    fetchListRequest: (page, filters) => ({
        type: PRODUCTS_ACTION_TYPES.FETCH_LIST_REQUEST,
        payload: { page, filters },
    }),

    fetchListSuccess: (data, page, total, totalPages, hasMore) => ({
        type: PRODUCTS_ACTION_TYPES.FETCH_LIST_SUCCESS,
        payload: { data, page, total, totalPages, hasMore },
        meta: { timestamp: Date.now() },
    }),

    fetchListFailure: (error) => ({
        type: PRODUCTS_ACTION_TYPES.FETCH_LIST_FAILURE,
        payload: { error },
        error: true,
    }),
}

export const fetchProductsList = (options = {}) => {
    return async (dispatch, getState) => {
        const { page = 1, refresh = false, filters = {} } = options;
        const state = getState();
        const productsState = state.products;

        if (productsState.loading.list && !refresh) {
            return;
        }

        const regionKladr = geoSelectors.selectRegionKladr(state);

        const currentFilters = {
            ...productsState.filters,
            ...filters,
        };

        dispatch(productsActions.fetchListRequest(page, currentFilters));

        try {
            const result = await productsApi.fetchList({
                page,
                per_page: PRODUCTS_CONFIG.PAGE_SIZE,
                search: currentFilters.search || '',
                category: currentFilters.categoryId || '',
                order_by: currentFilters.orderBy || '',
                order: currentFilters.order || '',
                region: regionKladr,
            });

            const hasMore = page < result.totalPages;

            dispatch(productsActions.fetchListSuccess(
                result.data,
                page,
                result.total,
                result.totalPages,
                hasMore
            ));
        } catch (error) {
            console.error('Products fetch error:', error);
            dispatch(productsActions.fetchListFailure(error.message));
            toastr.error('Ошибка', `Не удалось загрузить товары: ${error.message}`);
        }
    }
}

export const fetchProductsByCategory = (categoryId, options = {}) => {
    return async (dispatch, getState) => {
        const {
            limit = PRODUCTS_CONFIG.SHOWCASE_SIZE,
            page = 1,
            refresh = false
        } = options;

        const state = getState();
        const regionKladr = geoSelectors.selectRegionKladr(state);

        dispatch({
            type: PRODUCTS_ACTION_TYPES.FETCH_CATEGORY_REQUEST,
            payload: { categoryId, page, refresh },
        });

        try {
            const result = await productsApi.fetchList({
                category: categoryId,
                per_page: limit,
                page: page,
                region: regionKladr,
            });

            const currentPage = result.page || page;
            const hasMore = currentPage < result.totalPages;

            dispatch({
                type: PRODUCTS_ACTION_TYPES.FETCH_CATEGORY_SUCCESS,
                payload: {
                    categoryId,
                    data: result.data,
                    total: result.total,
                    totalPages: result.totalPages,
                    currentPage: currentPage,
                    hasMore,
                    perPage: limit,
                },
                meta: { timestamp: Date.now() },
            });
        } catch (error) {
            console.error(`Category ${categoryId} fetch error:`, error);
            dispatch({
                type: PRODUCTS_ACTION_TYPES.FETCH_CATEGORY_FAILURE,
                payload: { categoryId, error: error.message },
            });
            toastr.error('Ошибка', `Не удалось загрузить товары категории ${categoryId}`);
        }
    };
};

export const fetchNextCategoryPage = (categoryId) => {
    return (dispatch, getState) => {
        const state = getState().products;
        const categoryInfo = state.categoriesLoaded?.[categoryId];

        if (!categoryInfo) {
            return;
        }

        if (state.loading.categories?.[categoryId]) {
            return;
        }

        if (!categoryInfo.hasMore) {
            return;
        }

        const nextPage = categoryInfo.currentPage + 1;
        const perPage = categoryInfo.perPage || PRODUCTS_CONFIG.SHOWCASE_SIZE;

        dispatch(fetchProductsByCategory(categoryId, {
            page: nextPage,
            limit: perPage,
        }));
    };
};


export const fetchMultipleCategories = (categoryIds, options = {}) => {
    return async (dispatch) => {
        const promises = categoryIds.map(categoryId =>
            dispatch(fetchProductsByCategory(categoryId, options))
        );

        await Promise.all(promises);
    };
};

export const searchProducts = (query) => {
    return async (dispatch, getState) => {
        if (!query || query.trim() === '') {
            dispatch({ type: PRODUCTS_ACTION_TYPES.CLEAR_SEARCH });
            return;
        }

        const state = getState();
        const regionKladr = geoSelectors.selectRegionKladr(state);

        dispatch({ type: PRODUCTS_ACTION_TYPES.SEARCH_REQUEST });

        try {
            const result = await productsApi.fetchList({
                search: query,
                page: 1,
                per_page: 8,
                region: regionKladr,
            });

            dispatch({
                type: PRODUCTS_ACTION_TYPES.SEARCH_SUCCESS,
                payload: { data: result.data }
            });
        } catch (error) {
            console.error('Search error:', error);
            dispatch({
                type: PRODUCTS_ACTION_TYPES.SEARCH_FAILURE,
                payload: { error: error.message }
            });
        }
    };
};


export const fetchProductById = (id, options = {}) => {
    return async (dispatch, getState) => {
        const { refresh = false } = options;
        const state = getState();

        // const existingProduct = selectProductById(state, id);

        // if (existingProduct && !refresh) {
        //     return;
        // }

        // if (state.products.loading.items[id]) {
        //     return;
        // }

        dispatch({
            type: PRODUCTS_ACTION_TYPES.FETCH_ITEM_REQUEST,
            payload: { id },
        });

        const regionKladr = geoSelectors.selectRegionKladr(state);
        const params = {};
        if (regionKladr) {
            params.region = regionKladr;
        }

        try {
            const data = await productsApi.fetchById(id, params);

            dispatch({
                type: PRODUCTS_ACTION_TYPES.FETCH_ITEM_SUCCESS,
                payload: { data, id },
                meta: { timestamp: Date.now() },
            });
        } catch (error) {
            console.error(`Product ${id} fetch error:`, error);
            dispatch({
                type: PRODUCTS_ACTION_TYPES.FETCH_ITEM_FAILURE,
                payload: { id, error: error.message },
            });
        }
    };
};

export const fetchRelatedProducts = (productId, limit = 4) => {
    return async (dispatch, getState) => {
        dispatch({
            type: PRODUCTS_ACTION_TYPES.FETCH_RELATED_REQUEST,
            payload: { productId },
        });

        try {
            const data = await productsApi.fetchRelatedProducts(productId, limit);

            dispatch({
                type: PRODUCTS_ACTION_TYPES.FETCH_RELATED_SUCCESS,
                payload: { productId, data },
                meta: { timestamp: Date.now() },
            });
        } catch (error) {
            console.error(`Related products for ${productId} fetch error:`, error);
            dispatch({
                type: PRODUCTS_ACTION_TYPES.FETCH_RELATED_FAILURE,
                payload: { productId, error: error.message },
            });
        }
    };
};

export const clearSearch = () => ({
    type: PRODUCTS_ACTION_TYPES.CLEAR_SEARCH
});